/**
 * 📈 EarningsChart Component
 * Revenue and commission visualization for the Affiliate Dashboard
 */

import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

// ============================================
// TYPES
// ============================================

export interface EarningsDataPoint {
  month: string;
  earnings: number;
  commissions: number;
}

interface EarningsChartProps {
  data: EarningsDataPoint[];
  title?: string;
  subtitle?: string;
  showPeriodSelector?: boolean;
  onPeriodChange?: (period: string) => void;
  selectedPeriod?: string;
  isLoading?: boolean;
  className?: string;
  trend?: {
    value: number;
    label: string;
  };
}

// ============================================
// CUSTOM TOOLTIP
// ============================================

interface TooltipPayload {
  value: number;
  name: string;
  color: string;
}

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600">
              {entry.name === 'earnings' ? 'Umsatz' : 'Provision'}
            </span>
          </div>
          <span className="font-semibold">
            €{entry.value.toLocaleString('de-DE')}
          </span>
        </div>
      ))}
    </div>
  );
};

// ============================================
// CUSTOM LEGEND
// ============================================

const CustomLegend: React.FC = () => (
  <div className="flex justify-center gap-6 mt-4">
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-[#009544]" />
      <span className="text-sm text-gray-600">Umsatz (Meine Lokale)</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-[#22c55e]" />
      <span className="text-sm text-gray-600">Meine Provision</span>
    </div>
  </div>
);

// ============================================
// TREND INDICATOR
// ============================================

const TrendIndicator: React.FC<{ value: number; label: string }> = ({ value, label }) => {
  const isPositive = value > 0;
  const isNeutral = value === 0;
  const Icon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;
  
  return (
    <div className={cn(
      'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
      isPositive && 'bg-green-50 text-green-700',
      isNeutral && 'bg-gray-100 text-gray-600',
      !isPositive && !isNeutral && 'bg-red-50 text-red-700'
    )}>
      <Icon className="w-3.5 h-3.5" />
      <span>{isPositive ? '+' : ''}{value}% {label}</span>
    </div>
  );
};

// ============================================
// PERIOD SELECTOR
// ============================================

const periods = [
  { value: '3m', label: '3 Monate' },
  { value: '6m', label: '6 Monate' },
  { value: '12m', label: '12 Monate' },
  { value: 'ytd', label: 'Dieses Jahr' },
];

// ============================================
// MAIN COMPONENT
// ============================================

export const EarningsChart: React.FC<EarningsChartProps> = ({
  data,
  title = 'Einnahmen-Entwicklung',
  subtitle,
  showPeriodSelector = true,
  onPeriodChange,
  selectedPeriod = '6m',
  isLoading = false,
  className,
  trend,
}) => {
  const [period, setPeriod] = useState(selectedPeriod);

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    onPeriodChange?.(value);
  };

  // Calculate totals for header
  const totalEarnings = data.reduce((sum, d) => sum + d.earnings, 0);
  const totalCommissions = data.reduce((sum, d) => sum + d.commissions, 0);

  if (isLoading) {
    return (
      <Card className={cn('', className)}>
        <CardContent className="p-5">
          <div className="animate-pulse">
            <div className="h-5 bg-gray-200 rounded w-40 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-60 mb-6" />
            <div className="h-[280px] bg-gray-100 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('bg-white/80 backdrop-blur-xl border-white/60 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden', className)}>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-3 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              {title}
              {trend && <TrendIndicator value={trend.value} label={trend.label} />}
            </CardTitle>
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
            <div className="flex gap-6 mt-3">
              <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-gray-50 to-white">
                <p className="text-xs text-gray-500">Gesamtumsatz</p>
                <p className="text-lg font-bold bg-gradient-to-br from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  €{totalEarnings.toLocaleString('de-DE')}
                </p>
              </div>
              <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-emerald-50 to-white">
                <p className="text-xs text-gray-500">Provision</p>
                <p className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
                  €{totalCommissions.toLocaleString('de-DE')}
                </p>
              </div>
            </div>
          </div>
          {showPeriodSelector && (
            <Select value={period} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {data.length === 0 ? (
          <div className="h-[280px] flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900">Keine Daten verfügbar</p>
              <p className="text-xs text-gray-500 mt-1">
                Sobald deine Lokale Umsatz machen, siehst du hier die Entwicklung.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="h-[280px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#009544" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#009544" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorCommissions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="#e5e7eb" 
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    tickFormatter={(value) => `€${value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value}`}
                    dx={-10}
                    width={50}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="earnings"
                    stroke="#009544"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorEarnings)"
                    name="earnings"
                  />
                  <Area
                    type="monotone"
                    dataKey="commissions"
                    stroke="#22c55e"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCommissions)"
                    name="commissions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <CustomLegend />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EarningsChart;
